import axios from 'axios';
import { EventSource } from '../analyticsService';
import { PARTNER_REGISTRATION_FORM_ID, REWARD_BUTTON_ID, REWARD_POPUP_ID } from './constants';
import { ContentActionsEvent, ContentType, EventType, FieldName, RelationType } from './types';
const envPeriod = process.env.REACT_APP_ANALYTICS_REQUESTS_PERIOD;
const errorExtendTime = process.env.REACT_APP_ANALYTICS_ERROR_EXTEND_TIME;
const envBulkSize = process.env.REACT_APP_ANALYTICS_BULK_SIZE;
const envMaxEventHistorySize = process.env.REACT_APP_ANALYTICS_MAX_EVENT_HISTORY_SIZE;
const DEFAULT_PERIOD = envPeriod ? parseInt(envPeriod, 10) : 2000;
const DEFAULT_BULK_SIZE = envBulkSize ? parseInt(envBulkSize, 10) : 100;
const MAX_EVENT_HISTORY_SIZE = envMaxEventHistorySize ? parseInt(envMaxEventHistorySize, 10) : 1000;
const ERROR_EXTEND_TIME = errorExtendTime ? parseInt(errorExtendTime, 10) : 2000;

class ContentActionsService {
    private events: ContentActionsEvent[] = [];
    private bulkSize: number = DEFAULT_BULK_SIZE;
    private intervalPeriod = DEFAULT_PERIOD;

    constructor(maxBulkSize: number = DEFAULT_BULK_SIZE) {
        this.bulkSize = maxBulkSize;
        this.sendBulk();
    }
    private pushEvent = (event: ContentActionsEvent) => {
        this.events.push({ ...event, time: new Date().toISOString(), platform: 'web' });
        if (this.events.length > MAX_EVENT_HISTORY_SIZE) {
            this.events.shift();
        }
    };

    private sendBulk = async () => {
        let lastBulk: ContentActionsEvent[] = [];

        try {
            lastBulk = this.events.splice(Math.max(this.events.length - 1 - this.bulkSize, 0));
            if (lastBulk.length) {
                await axios.post('/content/actions', {
                    events: lastBulk,
                });
                this.intervalPeriod = DEFAULT_PERIOD;
            }
            setTimeout(() => this.sendBulk(), this.intervalPeriod);
        } catch (err) {
            if (axios.isAxiosError(err) && err.response?.status !== 400) {
                if (lastBulk.length) {
                    this.events.push(...lastBulk);
                }

                this.intervalPeriod += ERROR_EXTEND_TIME;
            }
            setTimeout(() => this.sendBulk(), this.intervalPeriod);
        }
    };

    public trackCollectionEdit(params: {
        id: string;
        newValue: string;
        oldValue?: string;
        fieldName: FieldName;
        screen: EventSource;
        relatedContentType: ContentType;
    }) {
        this.pushEvent({
            event_type: EventType.COLLECTION_EDIT,
            screen: params.screen,
            related_content: [
                {
                    relation_type: RelationType.COLLECTION_ITEM,
                    content: {
                        id: params.id,
                        type: params.relatedContentType,
                    },
                },
            ],
            field_changes: [
                {
                    field_name: params.fieldName,
                    old_value: params.oldValue,
                    new_value: params.newValue,
                },
            ],
        });
    }

    public trackCollectionEditDialogOpen(params: { id: string; screen: EventSource }) {
        this.pushEvent({
            event_type: EventType.COLLECTION_EDIT_DIALOG_OPEN,
            screen: params.screen,
            related_content: [
                {
                    relation_type: RelationType.CONTAINER,
                    content: {
                        id: params.id,
                        type: ContentType.COLLECTION,
                    },
                },
            ],
        });
    }

    public trackCollectionEditDialogClose(params: { id: string; screen: EventSource }) {
        this.pushEvent({
            event_type: EventType.COLLECTION_EDIT_DIALOG_CLOSE,
            screen: params.screen,
            related_content: [
                {
                    relation_type: RelationType.CONTAINER,
                    content: {
                        id: params.id,
                        type: ContentType.COLLECTION,
                    },
                },
            ],
        });
    }

    public trackCollectionCreateDialogOpen(params: { id: string; screen: EventSource }) {
        this.pushEvent({
            event_type: EventType.COLLECTION_CREATE_DIALOG_OPEN,
            screen: params.screen,
            related_content: [
                {
                    relation_type: RelationType.COLLECTION_ITEM,
                    content: {
                        id: params.id,
                        type: ContentType.POST,
                    },
                },
            ],
        });
    }

    public trackPostSuggestsGenerate(params: { id: string; screen: EventSource }) {
        this.pushEvent({
            event_type: EventType.GENERATE_SUGGESTS,
            screen: params.screen,
            related_content: [
                {
                    relation_type: RelationType.COLLECTION_ITEM,
                    content: {
                        id: params.id,
                        type: ContentType.POST,
                    },
                },
            ],
        });
    }

    public trackCollectionSuggestsGenerate(params: { id: string; screen: EventSource }) {
        this.pushEvent({
            event_type: EventType.GENERATE_SUGGESTS,
            screen: params.screen,
            related_content: [
                {
                    relation_type: RelationType.CONTAINER,
                    content: {
                        id: params.id,
                        type: ContentType.COLLECTION,
                    },
                },
            ],
        });
    }

    public trackSaveToCollectionDialogOpen(params: { id: string; screen: EventSource }) {
        this.pushEvent({
            event_type: EventType.SAVE_TO_COLLECTION_DIALOG_OPEN,
            screen: params.screen,
            related_content: [
                {
                    relation_type: RelationType.COLLECTION_ITEM,
                    content: {
                        id: params.id,
                        type: ContentType.POST,
                    },
                },
            ],
        });
    }

    public trackSaveToCollectionDialogClose(params: { id: string; screen: EventSource }) {
        this.pushEvent({
            event_type: EventType.SAVE_TO_COLLECTION_DIALOG_CLOSE,
            screen: params.screen,
            related_content: [
                {
                    relation_type: RelationType.COLLECTION_ITEM,
                    content: {
                        id: params.id,
                        type: ContentType.POST,
                    },
                },
            ],
        });
    }

    public trackCollectionUpdated(params: {
        id: string;
        screen: EventSource;
        oldValues: { displayName?: string; showInFeed?: boolean; isPublic?: boolean };
        newValues: { displayName: string; showInFeed?: boolean; isPublic?: boolean };
    }) {
        this.pushEvent({
            event_type: EventType.COLLECTION_UPDATED,
            screen: params.screen,
            related_content: [
                {
                    relation_type: RelationType.CONTAINER,
                    content: {
                        id: params.id,
                        type: ContentType.COLLECTION,
                    },
                },
            ],
            field_changes: [
                {
                    field_name: FieldName.DISPLAY_NAME,
                    old_value: params.oldValues?.displayName,
                    new_value: params.newValues?.displayName,
                },
                {
                    field_name: FieldName.SHOW_IN_FEED,
                    old_value: String(params.oldValues?.showInFeed ?? false),
                    new_value: String(params.newValues?.showInFeed ?? false),
                },
                {
                    field_name: FieldName.IS_PUBLIC,
                    old_value: String(params.oldValues?.isPublic ?? false),
                    new_value: String(params.newValues?.isPublic ?? false),
                },
            ],
        });
    }

    public trackCollectionCreated(params: {
        id: string;
        postIds: string[];
        screen: EventSource;
        newValues: { displayName: string; showInFeed?: boolean; isPublic?: boolean };
    }) {
        this.pushEvent({
            event_type: EventType.COLLECTION_CREATED,
            screen: params.screen,
            related_content: [
                {
                    relation_type: RelationType.CONTAINER,
                    content: {
                        id: params.id,
                        type: ContentType.COLLECTION,
                    },
                },
                ...params.postIds.map((postId) => ({
                    relation_type: RelationType.COLLECTION_ITEM,
                    content: {
                        id: postId,
                        type: ContentType.POST,
                    },
                })),
            ],
            field_changes: [
                {
                    field_name: FieldName.DISPLAY_NAME,
                    new_value: params.newValues?.displayName,
                },
                {
                    field_name: FieldName.SHOW_IN_FEED,
                    new_value: String(params.newValues?.showInFeed ?? false),
                },
                {
                    field_name: FieldName.IS_PUBLIC,
                    new_value: String(params.newValues?.isPublic ?? false),
                },
            ],
        });
    }

    public trackAddPostToCollection(params: {
        postId: string;
        collectionId: string;
        screen: EventSource;
    }) {
        this.pushEvent({
            event_type: EventType.POST_TO_COLLECTION_ADDED,
            screen: params.screen,
            related_content: [
                {
                    relation_type: RelationType.CONTAINER,
                    content: {
                        id: params.collectionId,
                        type: ContentType.COLLECTION,
                    },
                },
                {
                    relation_type: RelationType.COLLECTION_ITEM,
                    content: {
                        id: params.postId,
                        type: ContentType.POST,
                    },
                },
            ],
        });
    }

    // Трекеры для регистрации ЕПДО ниже

    public trackPartnerRegistrationPageOpen(params: { screen: EventSource }) {
        this.pushEvent({
            event_type: EventType.PARTNER_REGISTRATION_PAGE_OPEN,
            screen: params.screen,
            related_content: [
                {
                    relation_type: RelationType.FORM,
                    content: {
                        id: PARTNER_REGISTRATION_FORM_ID,
                        type: ContentType.FORM_PAGE,
                    },
                },
            ],
        });
    }

    public trackPartnerTypeTabClick(params: {
        partnerType: string;
        oldPartnerType?: string;
        screen: EventSource;
    }) {
        this.pushEvent({
            event_type: EventType.PARTNER_TYPE_TAB_CLICK,
            screen: params.screen,
            related_content: [
                {
                    relation_type: RelationType.FORM,
                    content: {
                        id: PARTNER_REGISTRATION_FORM_ID,
                        type: ContentType.TAB,
                    },
                },
            ],
            field_changes: [
                {
                    field_name: FieldName.PARTNER_TYPE,
                    old_value: params.oldPartnerType,
                    new_value: params.partnerType,
                },
            ],
        });
    }

    public trackPartnerRegistrationNextClick(params: {
        step: string;
        partnerType?: string;
        screen: EventSource;
    }) {
        this.pushEvent({
            event_type: EventType.PARTNER_REGISTRATION_NEXT_CLICK,
            screen: params.screen,
            related_content: [
                {
                    relation_type: RelationType.FORM,
                    content: {
                        id: PARTNER_REGISTRATION_FORM_ID,
                        type: ContentType.BUTTON,
                    },
                },
            ],
            extra_context: [
                {
                    key: 'step',
                    value: params.step,
                },
            ],
        });
    }

    public trackPartnerRegistrationSubmit(params: { step: string; screen: EventSource }) {
        this.pushEvent({
            event_type: EventType.PARTNER_REGISTRATION_SUBMIT,
            screen: params.screen,
            related_content: [
                {
                    relation_type: RelationType.FORM,
                    content: {
                        id: PARTNER_REGISTRATION_FORM_ID,
                        type: ContentType.BUTTON,
                    },
                },
            ],
            extra_context: [
                {
                    key: 'step',
                    value: params.step,
                },
            ],
        });
    }

    // Трекеры для кнопки получения награды

    public trackRewardButtonClick(params: {
        amount?: number;
        status?: string;
        screen: EventSource;
    }) {
        this.pushEvent({
            event_type: EventType.REWARD_BUTTON_CLICK,
            screen: params.screen,
            related_content: [
                {
                    relation_type: RelationType.REWARD,
                    content: {
                        id: REWARD_BUTTON_ID,
                        type: ContentType.BUTTON,
                    },
                },
            ],
            extra_context: params.status
                ? [
                      {
                          key: 'status',
                          value: params.status,
                      },
                  ]
                : undefined,
        });
    }

    public trackRewardDetailsClick(params: { screen: EventSource }) {
        this.pushEvent({
            event_type: EventType.REWARD_DETAILS_CLICK,
            screen: params.screen,
            related_content: [
                {
                    relation_type: RelationType.REWARD,
                    content: {
                        id: REWARD_POPUP_ID,
                        type: ContentType.BUTTON,
                    },
                },
            ],
        });
    }

    public trackRewardRegisterClick(params: { screen: EventSource }) {
        this.pushEvent({
            event_type: EventType.REWARD_REGISTER_CLICK,
            screen: params.screen,
            related_content: [
                {
                    relation_type: RelationType.REWARD,
                    content: {
                        id: REWARD_POPUP_ID,
                        type: ContentType.BUTTON,
                    },
                },
            ],
        });
    }
}

export const contentActionsService = new ContentActionsService(DEFAULT_BULK_SIZE);
